// vue
import { ref, computed, reactive, watch, onMounted } from 'vue';
import moment from 'moment';

// services
import BotsService from'@services/bots';
import StatsService from '@services/statsService';
import LocalStorage from '@services/ts/localStorage';

// shared
import { CRUD_PAGER_FACTORY } from '@shared/factories';

// i18n
import { useI18n } from 'vue-i18n';

// router
import { useRoute, useRouter } from 'vue-router';

// UI
import { useMessage, useNotification } from 'naive-ui';

// store
import { useGl } from '@store/ts/gl';
import { useEnv } from '@store/ts/env';
import { useRefs } from '@store/ts/refs';
import { useStats } from '@store/stats';
import { useConfirm } from '@store/confirm';

export default function (props, context) {
    // store
    const gl = useGl();
    const env = useEnv();
    const refs = useRefs();
    const stats = useStats();
    const confirm = useConfirm();
    
    // ranges
    const ranges = computed(() => refs.ranges);

    // router
    const route = useRoute();
    const router = useRouter();

    // i18n
    const { t } = useI18n();

    // vars
    const { _ } = window;
    const init = ref(false);
    const cyclesDataTable = ref();
    const loadings = reactive({
        table: false,
        clearCycles: false,
        tableOrders: false,
    });

    const storeFilterKey = '__tradestatistic_transactions_table_new';
    let storeFilters = LocalStorage.getItem(storeFilterKey);

    // period
    const localDefaultPeriod = ref();
    const defaultPeriod = moment().subtract(7, 'days').format('YYYY-MM-DD') + ' ' + moment().add(7, 'days').format('YYYY-MM-DD');
    const storagePeriod = () => {
        const i = ranges.value.findIndex(({ id }) => id == storeFilters?.filters.period);
    
        const result = ~i
            ? ranges.value[i].value.join(' ')
            : storeFilters?.filters.period || defaultPeriod;
    
        if (!localDefaultPeriod.value) {
            localDefaultPeriod.value = result;
        }
    
        return result;
    };

    // set filter settings
    if (storeFilters == null) {
        LocalStorage.setItem(storeFilterKey, CRUD_PAGER_FACTORY({
            bots: {},
            period: storagePeriod(),
            statuses: [0],
            exchanges: [-1],
            from: 'all',
        }, {
            perPage: 10,
        }));

        storeFilters = LocalStorage.getItem(storeFilterKey);
    }
  
    const filters = reactive({
        ...storeFilters,
    });

    const filtersChanged = computed(() => {
        return filters.filters.statuses[0] !== -1;
    });

    const resetFilters = () => {
        filters.filters.statuses = [0];
        filters.filters.bots = {};
        filters.filters.exchanges = [ -1 ];
        filters.filters.period = localDefaultPeriod.value || defaultPeriod;
              
        cyclesGet();
    };

    const periods = computed(() => {
        const arr = filters.filters.period.split(' ');

        return [new Date(arr[0]).getTime(), new Date(arr[1]).getTime()];
    });

    const refFilters = computed(() => [
        {
            mode: 'exchanges',
            title: refs.localization.filters.stats.filters_stats_exchanges_label,
            refs: refs.exchanges,
            filter: 'filters.exchanges',
            seeable: filters.filters.exchanges[0] !== -1,
        }, {
            mode: 'bots',
            title: refs.localization.filters.stats.filters_stats_bots_label,
            refs: stats.botFilters,
            filter: 'filters.bots',
            seeable: !!Object.keys(filters.filters.bots).length,
        }, {
            show: filters.filters.statuses[0] !== 0,
            type: 'render',
            mode: 'date_picker',
            value: periods.value,
            filter: 'filters.period',
            title: refs.localization.filters.stats.filters_stats_period_label,
        }, {
            seeable: filters.filters.statuses[0] != -1,
            setAll: false,
            mode: 'radio_group',
            title: stats.localization['trade_statistics_filters_statuses'],
            refs: stats.cycleFilters,
            value: filters.filters.statuses[0],
            filter: 'filters.statuses',
        },
    ]);

    const columns = computed(() => {
        if (cyclesDataTable.value?.columns) {
            const arr = [...cyclesDataTable.value.columns]
                .filter(el => el.title);
            return arr;
        } else return [];
    });

    const prepareColumns = prepare => {
        return prepare
            .filter(el => {
                return filters.filters.statuses[0] == 0 ? !Boolean(el?.isHiddenActive) : true;
            });
    };
    
    const cyclesGet = async () => {
        loadings.table = true;

        try {
            // save filters
            LocalStorage.setItem(storeFilterKey, filters);
        
            const payload = _.cloneDeep(filters);
            payload.filters.bots =
                Object.keys(filters.filters.bots).length
                    ? [].concat(...Object.values(filters.filters.bots))
                    : [-1];

            const prepare = await StatsService.cyclesGet({
                pager: payload,
            });
          
            const i = prepare.data.columns.findIndex(({ name }) => name == 'current_orders_info');

            if (~i) {
                prepare.data.columns.splice(i, 0, {
                    isSortable: true,
                    name: 'p_n_l',
                    title: 'PNL',
                });
            }

            prepare.data.columns = prepareColumns(prepare.data.columns);

            if (filters.filters.statuses[0] == 1) {
                prepare.data.records.sort((item1, item2) => {
                    const date1 = new Date(item1.date_end);
                    const date2 = new Date(item2.date_end);
                
                    return date2 - date1;
                });
            }

            if (Array.isArray(prepare.data.records)) {
                prepare.data.records.forEach((item, i) => {
                    const price = item.prices;
                    item.cycle_id = item.id;
  
                    if (price == null || price.includes('NA')) {
                        return;
                    }
  
                    const [course, fixPrice, zeroPrice] = price.split('\n');
                    let fixDelta = null;
                    let zeroDelta = null;
                    const isLong = item.algo == 'long';
                    if (!isLong) {
                        fixDelta = -Number(fixPrice) + Number(course);
                        zeroDelta = -Number(zeroPrice) + Number(course);
                    } else {
                        fixDelta = Number(fixPrice) - Number(course);
                        zeroDelta = Number(zeroPrice) - Number(course);
                    }
                    const fixPerc = ((fixDelta / Number(course)) * 100).toFixed(2);
                    const zeroPerc = ((zeroDelta / Number(course)) * 100).toFixed(2);
                    const newText = `${course}\n${fixPrice} (${fixPerc}%)\n${zeroPrice} (${zeroPerc}%)`;
                    prepare.data.records[i].prices_draft = newText;
                    item.r1 = +zeroPrice;
                    item.r2 = +fixPrice;
                    item.p_n_l = (((zeroDelta / Number(course)) * 100).toFixed(2)) * -1;
                });
              
                if (cyclesDataTable.value) {
                    Object.keys(prepare.data).forEach(key => {
                        if (!['stats'].includes(key)) {
                            cyclesDataTable.value[key] = prepare.data[key];
                        }
                    });
                } else {
                    cyclesDataTable.value = prepare.data;
                }
  
                cyclesDataTable.value.records.push({
                    type: 'footer',
                    ...cyclesDataTable.value.totals,
                });
            }
        } catch (e) {
            console.log(e);
            gl.showNotification({
                type: 'error',
                msg: t('errorMessage'),
            });
        };
  
        loadings.table = false;
    };

    const sortColumn = $event => {
        _.set(
            filters,
            'sort_column',
            $event.columnKey !== undefined && $event.order !== undefined
                ? $event.columnKey
                : '');

        _.set(
            filters,
            'sort_dir',
            $event.order !== undefined
                ? $event.order
                : '');

        cyclesGet();
    };

    const doSomething = ([ action, value ]) => {
        const fns = {
            setCurrentCycle: () => {
                router.push(
                    {
                        name: 'trade-statistic.management',
                        params: {
                            id: value.bot_id,
                            cycle_id: value.number_id,
                        },
                    });
            },

        };

        fns[action]();
    };

    const changeFilter = ([ key, value ], update = false) => {
        const canUpdate = key === 'query' && !value && cyclesDataTable.value.pager.query;
    
        if (key !== 'page') {
            _.set(filters, 'page', 1);
        }
    
        if (key === 'perPage') {
            LocalStorage.setItem('__filters_stats_transaction_period', value);
        }
    
        _.set(filters, key, value !== null && value !== undefined ? value : [ -1 ]);
                
        if (update || canUpdate)
            cyclesGet();
    };

    const downloadData = async format => {
        gl.showLoading = true;

        try {
            const { redirectTo } = ( await StatsService.cyclesGet({
                format,
                pager: filters,
            }) ).data;

            window.location.href = redirectTo;
        } catch {
            gl.showNotification({
                type: 'error',
                msg: t('errorMessage'),
            });
        };

        gl.showLoading = false;
    };

    const prepareFilters = async () => {
        const arr = Object.entries(route.query);
        arr.forEach(([ key, value]) => {
            if (key == 'period') {
                const date = refs.ranges.find(({ id }) => id == value)?.value;
                if (date) {
                    filters.filters[key] = date.join(' ');
                }
            } else {
                filters.filters[key] = [+value];   
            }
        });
    };

    onMounted(async () => {
        await prepareFilters();
        await cyclesGet();

        init.value = true;
    });

    return {
        init,
        stats,
        filters,
        refFilters,
        columns,
        loadings,
        storeFilterKey,
        cyclesDataTable,
        filtersChanged,
        cyclesGet,
        sortColumn,
        doSomething,
        resetFilters,
        changeFilter,
        downloadData,
    };
}